import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  inject,
  input,
  isDevMode,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { DeclarativeMapBase } from '../declarative-map-base/declarative-map-base.component';
import { DeclarativeMapMarkerTemplateDirective } from './declarative-map-marker-template.directive';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { computedPrevious } from 'ngxtension/computed-previous';
import { filter, takeUntil } from 'rxjs';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { MapPane } from '@cue/map/core';

@Component({
  selector: 'cue-declarative-map-marker',
  imports: [],
  templateUrl: './declarative-map-marker.component.html',
  styleUrl: './declarative-map-marker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeclarativeMapMarkerComponent extends DeclarativeMapBase implements OnDestroy {
  terminator = injectDestroy();

  focused = input(false);

  onFocusedChange = explicitEffect([this.focused], ([focused]) => {
    if (focused) {
      this.focus();
    }
  });

  viewContainerRef = inject(ViewContainerRef);
  pane = input<MapPane>();
  zIndexOffset = input<number>(0);
  className = input<string>();
  geoJson = input();
  template = contentChild(DeclarativeMapMarkerTemplateDirective, {
    read: TemplateRef,
  });
  click = outputFromObservable(
    this.adapter.layerClicked.pipe(
      takeUntil(this.terminator),
      filter((x) => x === this.identifier)
    )
  );

  appended = outputFromObservable(
    this.adapter.layerAppended.pipe(
      takeUntil(this.terminator),
      filter((x) => x === this.identifier)
    )
  );

  removed = outputFromObservable(
    this.adapter.layerRemoved.pipe(
      takeUntil(this.terminator),
      filter((x) => x === this.identifier)
    )
  );

  instantiatedElement = signal<HTMLElement | undefined>(undefined);

  createElement = explicitEffect([this.template], () => {
    if (this.template()) {
      const viewRef = this.viewContainerRef.createEmbeddedView(this.template());
      // V DEV modu pro lepsi debug, ukryji do elementu instanci
      if (isDevMode()) {
        (viewRef.rootNodes[0] as any).compRef = this;
      }
      this.instantiatedElement.set(viewRef.rootNodes[0] as HTMLElement);
    } else {
      this.instantiatedElement.set(undefined);
    }
  });

  markerProperties = computed(() => ({
    pane: this.pane(),
    zIndexOffset: this.zIndexOffset(),
    className: this.className(),
    geoJson: this.geoJson(),
    element: this.instantiatedElement(),
  }));

  previousMarkerProperties = computedPrevious(this.markerProperties);

  affectMarker = explicitEffect([this.markerProperties, this.previousMarkerProperties], () => {
    if (!this.#markerAdded) {
      this.addMarker();
      this.#markerAdded = true;
    } else {
      this.updateMarker();
    }
  });

  #markerAdded = false;

  buildPatch() {
    const previous = this.previousMarkerProperties();
    const current = this.markerProperties();
    const patch = {} as Partial<typeof current>;
    if (previous) {
      if (previous.pane !== current.pane) {
        patch.pane = current.pane;
      }
      if (previous.zIndexOffset !== current.zIndexOffset || current.zIndexOffset !== undefined) {
        patch.zIndexOffset = current.zIndexOffset;
      }
      if (previous.className !== current.className || current.className !== undefined) {
        patch.className = current.className;
      }

      if (previous.geoJson !== current.geoJson) {
        patch.geoJson = current.geoJson;
      }
      if (previous.element !== current.element) {
        patch.element = current.element;
      }
    } else {
      Object.assign(patch, current);
    }
    return patch;
  }

  addMarker() {
    this.adapter.addMarker(this.identifier, this.markerProperties(), this.focused());
  }

  updateMarker() {
    const patch = this.buildPatch();
    this.adapter.editMarker(this.identifier, patch);
    if (this.focused()) {
      this.focus();
    }
  }

  ngOnDestroy() {
    this.adapter.removeMarker(this.identifier);
  }

  focus(align?: 'top-center') {
    this.adapter.focusMarker(this.identifier, align);
  }
}
